import React from 'react';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from './TdarrTableRowCell';

const detailColumns = [
  { name: 'type', label: 'Type', isVisible: true, isSortable: false },
  { name: 'details', label: 'Details', isVisible: true, isSortable: false }
];

const statusColumns = [
  { name: 'status', label: 'Status', isVisible: true, isSortable: false },
  { name: 'details', label: 'Details', isVisible: true, isSortable: false }
];

const workerTypes = [
  ['Transcode CPU', "Transcode worker which only does transcode/remux tasks which don't require a GPU."],
  ['Transcode GPU', "Transcode worker which only does transcode tasks which require a GPU. To determine if a task is 'GPU' based, the worker checks the transcode preset/args/terms for keywords relating to the selected GPU type. For example, on an 'nvenc' Tdarr Node, the worker will check for terms like 'nvenc', 'cuda' and 'cuvid'."],
  ['Health Check CPU', "Health check worker which only uses CPU. The 'Quick' health check type using HandBrake only runs on this worker type. An FFmpeg 'thorough' health check will run on this worker using CPU only."],
  ['Health Check GPU', "Health check worker which only uses GPU. 'Quick' health checks do not run on this worker type. An FFmpeg 'thorough' health check will run on this worker using GPU."]
];

const stagingStatuses = [
  ['Processing', 'The file is being processed by a worker.'],
  ['Review required', ''],
  ['Transcode cancelled', 'The transcode has been cancelled.'],
  ['Copy error', 'A different copy error occurred. Check the error for more details.'],
  ['Copy failed', 'Copying from the cache to the library/output folder failed. Make sure the cache and library drives are connected/accessible and that the server and node have access to the same transcode cache folder.'],
  ['Transcode error', 'The transcode encountered an error'],
  ['Queued', 'The task is waiting to be picked up by a worker'],
  ['Queued: Retrying', 'The task is waiting to be picked up by a worker'],
  ['Queued: Reviewed', ''],
  ['Require CPU Worker', 'The task is waiting to be picked up by any CPU-based worker'],
  ['Require GPU Worker', 'The task is waiting to be picked up by any hardware-based worker'],
  ['Require NVENC Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows NVENC tasks'],
  ['Require QSV Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows QSV tasks'],
  ['Require VAAPI Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows VAAPI tasks'],
  ['Require Videotoolbox Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows Videotoolbox tasks'],
  ['Require AMF Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows AMF tasks'],
  ['Require RKMPP Worker', 'The task is waiting to be picked up by a worker on a Tdarr Node which allows RKMPP tasks'],
  ['Accepted', 'The new file has been accepted and it will be copied to library/output folder'],
  ['Conditions Met', 'This will show if an output folder is being used and the file already meets the required plugin/transcode conditions'],
  ['Transcode success', 'The file has successfully finished transcoding (all plugin cycles completed)']
];

export function ServerOverviewInformation() {
  return <>
    <p>
      Monitor all your Tdarr servers in one place with Tdarr Relay.<br />
      Once you've added a key to your server, enable the setting below and go to <a href="https://home.tdarr.io/account/" target="_blank" rel="noreferrer">https://home.tdarr.io/account/</a> to see the status of your servers.
    </p>
    <p>Only basic numeric info is sent to your account: the pie chart data on the stats tab and the 24 hour worker verdict history graph. No file paths or sensitive data is sent.</p>
  </>;
}

export function NodesOverviewInformation() {
  return <>
    <p>This section contains information on each Tdarr Node which has connected to Tdarr Server.</p>
    <p>Use the worker +/- buttons to start up workers.</p>
    <Table columns={detailColumns}>
      <TableBody>
        {workerTypes.map(([type, details]) => <TableRow key={type}><TableRowCell>{type}</TableRowCell><TableRowCell>{details}</TableRowCell></TableRow>)}
      </TableBody>
    </Table>
  </>;
}

export function StagingInformation() {
  return <>
    <p>
      The staging section contains files which are currently being processed or are awaiting further action.<br />
      On the staged items, the 'Last completed cache file' is stored in the transcode cache.<br />
      The staged file limit is the maximum number of items allowed in the staging section, regardless of status.
    </p>
    <p>The following provides details on each status:</p>

    <Table columns={statusColumns}>
      <TableBody>
        {stagingStatuses.map(([status, details]) => <TableRow key={status}><TableRowCell>{status}</TableRowCell><TableRowCell>{details}</TableRowCell></TableRow>)}
      </TableBody>
    </Table>
    <p>Enable 'Auto accept successful transcodes' to automatically have new files moved to library/output folder upon transcode completion.</p>
    <p>The staged file limit sets a limit on the number of files allowed in the staging section. If the limit is reached, no new items will be taken from the queue tables below and added to the staging section.</p>
    <p>Note: The UI may show 'Copying' but a move operation will be attempted first before falling back to copy/delete.</p>
    <p>
      A cache cleaner service runs hourly and removes any dangling cache files which aren't in use and are older than 1 hour.<br />
      The cache browser allows viewing of Tdarr transcode cache files across all libraries.
    </p>
  </>;
}

export function StatusInformation() {
  return <>
    <p>All newly scanned files will be placed in the transcode and health check queues, unless the 'Hold for' option is used in the library settings in which case the items will be kept on the 'Hold' tab until the specified time.</p>
    <p>Files in the queues will be sent to available workers.</p>
    <p>For transcodes, if files already meet the plugin or transcoding requirements they will be marked as 'Not required' in 'Transcode: Success/Not required' tab.</p>
    <p>If files don't meet the requirements they will be transcoded. If transcoding is successful files will be marked as 'Transcode success' in the 'Transcode: Success/Not required' tab.</p>
    <p>If transcoding fails or is cancelled then files will be marked accordingly in the 'Transcode: Error/Cancelled' tab.</p>
    <br />
    <p><strong>The end goal of Tdarr is to be able to run it on your library and all items come out as 'Transcode:Not required', meaning nothing needed to be transcoded/remuxed etc.</strong></p>
  </>;
}
